import React from 'react';
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Rocket, Zap, Square, FlaskConical, Radio, Building2, Trash2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function Autotrade() {
  const params = new URLSearchParams(window.location.search);

  const [deployments, setDeployments] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [brokers, setBrokers] = React.useState([]);
  const [strategies, setStrategies] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedBroker, setSelectedBroker] = React.useState("ALL");
  const [form, setForm] = React.useState({
    strategy_id: params.get("strategy") || "",
    mode: "paper",
    broker_connection_id: "",
    multiplier: 1,
  });
  const [fireResult, setFireResult] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = async () => {
    setLoading(true);

    try {
      const [depRes, orderRes, brokerRes, strategyRes] = await Promise.all([
        api.get("/trading/deployments"),
        api.get("/trading/orders"),
        api.get("/brokers"),
        api.get("/strategies"),
      ]);

      setDeployments(Array.isArray(depRes.data) ? depRes.data : []);
      setOrders(Array.isArray(orderRes.data) ? orderRes.data : []);

      setBrokers(
        (Array.isArray(brokerRes.data) ? brokerRes.data : [])
          .filter((b) => b.status === "connected")
      );

      setStrategies(
        Array.isArray(strategyRes.data) ? strategyRes.data : []
      );
    } catch (e) {
      console.error("AutoTrading load failed", e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();

    return undefined;
  }, []);

  const refreshTradingData = async () => {
    try {
      const [depRes, orderRes] = await Promise.all([
        api.get("/trading/deployments"),
        api.get("/trading/orders"),
      ]);

      setDeployments(
        Array.isArray(depRes.data) ? depRes.data : []
      );

      setOrders(
        Array.isArray(orderRes.data) ? orderRes.data : []
      );
    } catch (e) {
      console.error("Trading data refresh failed", e);
    }
  };

  const deploy = async () => {
    if (!form.strategy_id) {
      window.alert("Select a strategy first.");
      return;
    }

    if (form.mode === "live" && !form.broker_connection_id) {
      window.alert("Select a connected broker for live trading.");
      return;
    }

    setBusy(true);

    try {
      const { data } = await api.post("/trading/deploy", form);

      if (data?.message) {
        window.alert(data.message);
      }

      setForm({
        strategy_id: "",
        mode: "paper",
        broker_connection_id: "",
        multiplier: 1,
      });

      await refreshTradingData();
    } catch (e) {
      window.alert(
        e.response?.data?.detail ||
        e.response?.data?.message ||
        "Deployment failed"
      );
    } finally {
      setBusy(false);
    }
  };

  const fire = async (id) => {
    if (!window.confirm("Fire a one-time test signal for this deployment?")) {
      return;
    }

    setBusy(true);

    try {
      const { data } = await api.post(
        "/trading/fire-signal",
        { deployment_id: id }
      );

      setFireResult(data);

      if (data?.routed_to === "broker") {
        window.alert(data.confirmation || "Order routed to broker.");
      } else {
        window.alert("Test signal completed. No live broker order was placed.");
      }

      await refreshTradingData();
    } catch (e) {
      window.alert(
        e.response?.data?.detail ||
        e.response?.data?.message ||
        "Fire signal failed"
      );
    } finally {
      setBusy(false);
    }
  };

  const stop = async (id) => {
    if (!window.confirm("Stop this deployment?")) return;

    try {
      await api.post(`/trading/deployments/${id}/stop`);
      await refreshTradingData();
    } catch (e) {
      window.alert(
        e.response?.data?.detail ||
        "Unable to stop deployment"
      );
    }
  };

  const removeDeployment = async (id) => {
    if (
      !window.confirm(
        "Delete this deployment and all its orders? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      await api.delete(`/trading/deployments/${id}`);
      await refreshTradingData();
    } catch (e) {
      window.alert(
        e.response?.data?.detail ||
        "Unable to delete deployment"
      );
    }
  };

  /*
   * Build a stable broker key.
   *
   * Priority:
   * 1. broker_connection_id
   * 2. broker id
   * 3. broker_name
   * 4. Paper Trading
   */
  const brokerKey = (x) => {
    if (!x) return "paper";

    return String(
      x.broker_connection_id ||
      x.broker_id ||
      x.broker ||
      x.broker_name ||
      (x.mode === "paper" ? "paper" : "unknown")
    );
  };

  const brokerLabel = (x) => {
    if (!x) return "Paper Trading";

    return (
      x.broker_name ||
      x.broker_label ||
      x.label ||
      x.broker ||
      (x.mode === "paper" ? "Paper Trading" : "Broker")
    );
  };

  /*
   * Map deployment IDs to broker groups.
   * This lets an order stay with the deployment/broker even if
   * the order itself does not contain broker_name.
   */
  const deploymentBrokerMap = {};

  deployments.forEach((d) => {
    deploymentBrokerMap[d.id] = brokerKey(d);
  });

  const deploymentBrokerLabelMap = {};

  deployments.forEach((d) => {
    deploymentBrokerLabelMap[d.id] = brokerLabel(d);
  });

  const orderGroupKey = (o) => {
    if (o?.deployment_id && deploymentBrokerMap[o.deployment_id]) {
      return deploymentBrokerMap[o.deployment_id];
    }

    return brokerKey(o);
  };

  const orderGroupLabel = (o) => {
    if (
      o?.deployment_id &&
      deploymentBrokerLabelMap[o.deployment_id]
    ) {
      return deploymentBrokerLabelMap[o.deployment_id];
    }

    return brokerLabel(o);
  };

  /*
   * Connected brokers first.
   * Then include brokers which currently have deployments/orders.
   */
  const brokerGroups = [];

  const seen = new Set();

  const addGroup = (key, label, connection = null) => {
    if (!key || seen.has(key)) return;

    seen.add(key);

    brokerGroups.push({
      key,
      label,
      connection,
    });
  };

  brokers.forEach((b) => {
    addGroup(
      b.id,
      b.label || b.name || b.broker,
      b
    );
  });

  deployments.forEach((d) => {
    addGroup(
      brokerKey(d),
      brokerLabel(d),
      null
    );
  });

  orders.forEach((o) => {
    addGroup(
      orderGroupKey(o),
      orderGroupLabel(o),
      null
    );
  });

  const visibleGroups =
    selectedBroker === "ALL"
      ? brokerGroups
      : brokerGroups.filter(
          (g) => g.key === selectedBroker
        );

  const deploymentForGroup = (key) =>
    deployments.filter(
      (d) => brokerKey(d) === key
    );

  const ordersForGroup = (key) =>
    orders.filter(
      (o) => orderGroupKey(o) === key
    );

  const statusBadge = (status) => {
    const running =
      String(status || "").toLowerCase() === "running";

    return (
      <span
        className={
          running
            ? "inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-emerald-50 text-emerald-700"
            : "inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold bg-slate-100 text-slate-500"
        }
      >
        <span>{running ? "●" : "●"}</span>
        {running ? "LIVE / RUNNING" : String(status || "STOPPED").toUpperCase()}
      </span>
    );
  };

  const orderStatusBadge = (o) => {
    const confirmed = !!o?.broker_confirmed;

    return (
      <span
        className={
          confirmed
            ? "inline-flex px-2 py-1 text-[10px] font-bold bg-emerald-50 text-emerald-700"
            : "inline-flex px-2 py-1 text-[10px] font-bold bg-slate-100 text-slate-500"
        }
      >
        {o?.status ||
          (confirmed ? "PLACED_ON_BROKER" : "SIMULATED")}
      </span>
    );
  };

  const money = (v) => {
    if (v === null || v === undefined || v === "") return "—";

    const n = Number(v);

    if (!Number.isFinite(n)) return String(v);

    return `₹${n.toFixed(2)}`;
  };

  return (
    <Layout>
    <div className="space-y-6">

      <PageHeader
        title="Auto Trading"
        subtitle="Deployments and trades grouped by attached broker"
      />

      {/* Broker filter */}
      <div className="border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">

          <button
            onClick={() => setSelectedBroker("ALL")}
            className={
              selectedBroker === "ALL"
                ? "px-4 py-2 bg-slate-900 text-white text-xs font-bold"
                : "px-4 py-2 bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200"
            }
          >
            ALL BROKERS
          </button>

          {brokerGroups.map((g) => (
            <button
              key={g.key}
              onClick={() => setSelectedBroker(g.key)}
              className={
                selectedBroker === g.key
                  ? "px-4 py-2 bg-slate-900 text-white text-xs font-bold"
                  : "px-4 py-2 bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200"
              }
            >
              {g.label}
            </button>
          ))}

        </div>
      </div>

      {/* Broker sections */}
      {loading ? (
        <div className="border border-slate-200 bg-white p-12 text-center text-sm text-slate-400">
          Loading Auto Trading...
        </div>
      ) : visibleGroups.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400">
          No connected brokers, deployments or trades found.
        </div>
      ) : (
        <div className="space-y-6">

          {visibleGroups.map((group) => {
            const groupDeployments =
              deploymentForGroup(group.key);

            const groupOrders =
              ordersForGroup(group.key);

            const connected =
              !!group.connection ||
              groupDeployments.some(
                (d) => d.mode === "live"
              );

            return (
              <section
                key={group.key}
                className="border border-slate-200 bg-white"
                data-testid={`broker-section-${group.key}`}
              >

                {/* Broker header */}
                <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">

                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="font-heading font-bold text-lg">
                        {group.label}
                      </h2>

                      <span
                        className={
                          connected
                            ? "px-2 py-1 text-[10px] font-bold bg-emerald-50 text-emerald-700"
                            : "px-2 py-1 text-[10px] font-bold bg-slate-100 text-slate-500"
                        }
                      >
                        {connected ? "● CONNECTED" : "● ACTIVITY"}
                      </span>
                    </div>

                    <div className="text-xs text-slate-500 mt-1">
                      {groupDeployments.length} deployment
                      {groupDeployments.length === 1 ? "" : "s"}
                      {" · "}
                      {groupOrders.length} trade
                      {groupOrders.length === 1 ? "" : "s"}
                    </div>
                  </div>

                  <div className="flex gap-2 text-xs font-mono">
                    <span className="px-3 py-2 bg-slate-50 border border-slate-200">
                      DEPLOYMENTS {groupDeployments.length}
                    </span>
                    <span className="px-3 py-2 bg-slate-50 border border-slate-200">
                      TRADES {groupOrders.length}
                    </span>
                  </div>

                </div>

                {/* Two columns */}
                <div className="grid grid-cols-1 xl:grid-cols-2 divide-y xl:divide-y-0 xl:divide-x divide-slate-200">

                  {/* DEPLOYMENTS */}
                  <div className="p-5">

                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">
                        Deployments
                      </h3>

                      <span className="text-[10px] font-mono text-slate-400">
                        {groupDeployments.length}
                      </span>
                    </div>

                    {groupDeployments.length === 0 ? (
                      <div className="border border-dashed border-slate-200 p-8 text-center text-xs text-slate-400">
                        No deployments for this broker.
                      </div>
                    ) : (
                      <div className="space-y-3">

                        {groupDeployments.map((d) => (
                          <div
                            key={d.id}
                            className="border border-slate-200 p-4"
                            data-testid={`deployment-${d.id}`}
                          >

                            <div className="flex items-start justify-between gap-3">

                              <div className="min-w-0">
                                <div className="font-bold text-sm truncate">
                                  {d.strategy_name ||
                                    d.strategy ||
                                    d.name ||
                                    "Strategy"}
                                </div>

                                <div
                                  className="text-xs text-slate-500 mt-1"
                                  data-testid={`deployment-broker-${d.id}`}
                                >
                                  {d.broker_name ||
                                    group.label ||
                                    (d.mode === "live"
                                      ? "Broker not set"
                                      : "Paper Trading")}
                                </div>

                                {d.schedule && (
                                  <div className="text-[11px] text-sky-700 mt-1 font-mono">
                                    {d.schedule}
                                  </div>
                                )}

                                {d.last_entry_date && (
                                  <div className="text-[9px] font-mono text-emerald-600 mt-1">
                                    ENTERED{" "}
                                    {d.last_entry_date ===
                                    new Date().toISOString().slice(0, 10)
                                      ? "TODAY"
                                      : d.last_entry_date}
                                  </div>
                                )}
                              </div>

                              <div className="shrink-0">
                                {statusBadge(d.status)}
                              </div>

                            </div>

                            <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-slate-100">

                              <div>
                                <div className="text-[10px] uppercase text-slate-400">
                                  Mode
                                </div>
                                <div className="font-mono text-xs mt-1">
                                  {String(d.mode || "paper").toUpperCase()}
                                </div>
                              </div>

                              <div>
                                <div className="text-[10px] uppercase text-slate-400">
                                  Orders
                                </div>
                                <div className="font-mono text-xs mt-1">
                                  {d.orders_count ?? 0}
                                </div>
                              </div>

                              <div>
                                <div className="text-[10px] uppercase text-slate-400">
                                  Multiplier
                                </div>
                                <div className="font-mono text-xs mt-1">
                                  {d.multiplier ?? 1}×
                                </div>
                              </div>

                            </div>

                            <div className="flex flex-wrap gap-2 mt-4">

                              {d.status === "running" && (
                                <button
                                  onClick={() => stop(d.id)}
                                  disabled={busy}
                                  className="px-3 py-2 bg-slate-100 text-slate-700 text-[10px] font-bold hover:bg-slate-200 disabled:opacity-50"
                                >
                                  STOP
                                </button>
                              )}

                              <button
                                onClick={() => fire(d.id)}
                                disabled={
                                  busy ||
                                  d.status !== "running"
                                }
                                className="px-3 py-2 bg-slate-900 text-white text-[10px] font-bold disabled:opacity-40"
                                title={
                                  d.status !== "running"
                                    ? "Deployment is stopped"
                                    : "Fire a test signal"
                                }
                              >
                                FIRE TEST
                              </button>

                              <button
                                onClick={() =>
                                  removeDeployment(d.id)
                                }
                                disabled={busy}
                                className="px-3 py-2 border border-rose-200 text-rose-600 text-[10px] font-bold hover:bg-rose-50 disabled:opacity-50"
                              >
                                DELETE
                              </button>

                            </div>

                          </div>
                        ))}

                      </div>
                    )}

                  </div>

                  {/* TRADES */}
                  <div className="p-5">

                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">
                        Trades
                      </h3>

                      <span className="text-[10px] font-mono text-slate-400">
                        {groupOrders.length}
                      </span>
                    </div>

                    {groupOrders.length === 0 ? (
                      <div className="border border-dashed border-slate-200 p-8 text-center text-xs text-slate-400">
                        No trades for this broker.
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-slate-200">

                        <table className="w-full text-xs">

                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="px-3 py-3 text-left font-bold">
                                Strategy
                              </th>
                              <th className="px-3 py-3 text-left font-bold">
                                Symbol
                              </th>
                              <th className="px-3 py-3 text-left font-bold">
                                Side
                              </th>
                              <th className="px-3 py-3 text-right font-bold">
                                Qty
                              </th>
                              <th className="px-3 py-3 text-right font-bold">
                                Entry
                              </th>
                              <th className="px-3 py-3 text-right font-bold">
                                LTP
                              </th>
                              <th className="px-3 py-3 text-right font-bold">
                                P&L
                              </th>
                              <th className="px-3 py-3 text-left font-bold">
                                Status
                              </th>
                            </tr>
                          </thead>

                          <tbody>

                            {groupOrders.map((o, i) => {

                              const pnl =
                                o.pnl ??
                                o.unrealized_pnl ??
                                o.realized_pnl;

                              const pnlNum =
                                pnl === null ||
                                pnl === undefined
                                  ? null
                                  : Number(pnl);

                              return (
                                <tr
                                  key={
                                    o.id ||
                                    o.broker_order_id ||
                                    `${group.key}-${i}`
                                  }
                                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                                >

                                  <td className="px-3 py-3">
                                    <div className="font-medium max-w-[150px] truncate">
                                      {o.strategy_name ||
                                        o.strategy ||
                                        o.deployment_name ||
                                        "—"}
                                    </div>
                                  </td>

                                  <td className="px-3 py-3 font-mono whitespace-nowrap">
                                    {o.tradingsymbol ||
                                      o.symbol ||
                                      o.instrument ||
                                      "—"}
                                  </td>

                                  <td className="px-3 py-3">
                                    <span
                                      className={
                                        String(
                                          o.transaction_type ||
                                          o.side ||
                                          ""
                                        ).toUpperCase() === "BUY"
                                          ? "text-emerald-700 font-bold"
                                          : "text-rose-700 font-bold"
                                      }
                                    >
                                      {String(
                                        o.transaction_type ||
                                        o.side ||
                                        "—"
                                      ).toUpperCase()}
                                    </span>
                                  </td>

                                  <td className="px-3 py-3 text-right font-mono">
                                    {o.quantity ??
                                      o.qty ??
                                      "—"}
                                  </td>

                                  <td className="px-3 py-3 text-right font-mono whitespace-nowrap">
                                    {money(
                                      o.entry_price ??
                                      o.average_price ??
                                      o.price
                                    )}
                                  </td>

                                  <td className="px-3 py-3 text-right font-mono whitespace-nowrap">
                                    {money(
                                      o.ltp ??
                                      o.last_price
                                    )}
                                  </td>

                                  <td
                                    className={
                                      "px-3 py-3 text-right font-mono whitespace-nowrap " +
                                      (pnlNum !== null
                                        ? pnlNum >= 0
                                          ? "text-emerald-700"
                                          : "text-rose-700"
                                        : "text-slate-400")
                                    }
                                  >
                                    {money(pnl)}
                                  </td>

                                  <td className="px-3 py-3 whitespace-nowrap">
                                    {orderStatusBadge(o)}

                                    {o.broker_order_id && (
                                      <div className="text-[9px] font-mono text-slate-400 mt-1">
                                        #{o.broker_order_id}
                                      </div>
                                    )}
                                  </td>

                                </tr>
                              );
                            })}

                          </tbody>

                        </table>

                      </div>
                    )}

                  </div>

                </div>

              </section>
            );
          })}

        </div>
      )}

      {/* Deployment form */}
      <div className="border border-slate-200 bg-white p-5">

        <h3 className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-4">
          New Deployment
        </h3>

        <div className="grid md:grid-cols-4 gap-4">

          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              Strategy
            </label>

            <select
              data-testid="deploy-strategy"
              value={form.strategy_id}
              onChange={(e) =>
                setForm({
                  ...form,
                  strategy_id: e.target.value,
                })
              }
              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
            >
              <option value="">Select strategy...</option>

              {strategies.map((strategy) => (
                <option
                  key={strategy.id}
                  value={strategy.id}
                >
                  {strategy.name}
                  {strategy.underlying
                    ? ` (${strategy.underlying})`
                    : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              Mode
            </label>
            <select
              value={form.mode}
              onChange={(e) =>
                setForm({
                  ...form,
                  mode: e.target.value,
                })
              }
              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
            >
              <option value="paper">Paper</option>
              <option value="live">Live</option>
            </select>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              Broker
            </label>

            <select
              data-testid="deploy-broker"
              value={form.broker_connection_id}
              onChange={(e) =>
                setForm({
                  ...form,
                  broker_connection_id: e.target.value,
                })
              }
              className="mt-1 h-10 w-full border border-slate-300 px-2 text-sm bg-white"
            >
              <option value="">
                {form.mode === "live"
                  ? "Select connected broker…"
                  : "Optional for paper…"}
              </option>

              {brokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label || b.name || b.broker}
                </option>
              ))}
            </select>

            {brokers.length === 0 && (
              <p className="text-xs text-rose-500 mt-1">
                No connected broker. Connect one under Brokers.
              </p>
            )}
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              Multiplier
            </label>

            <input
              type="number"
              min="1"
              value={form.multiplier}
              onChange={(e) =>
                setForm({
                  ...form,
                  multiplier: Number(e.target.value || 1),
                })
              }
              className="mt-1 h-10 w-full border border-slate-300 px-3 text-sm"
            />
          </div>

        </div>

        <div className="flex justify-end mt-4">

          <button
            onClick={deploy}
            disabled={busy}
            className="px-5 py-3 bg-slate-900 text-white text-xs font-bold disabled:opacity-50"
          >
            {busy ? "WORKING..." : "DEPLOY STRATEGY"}
          </button>

        </div>

        {form.mode === "live" && (
          <p className="text-xs text-amber-600 text-center mt-3">
            Live mode routes real orders to the selected broker.
            Ensure broker credentials and live permissions are valid.
          </p>
        )}

      </div>

      {/* Fire result */}
      {fireResult && (
        <div className="border border-slate-200 bg-white p-5">

          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold">
              Latest Test Signal
            </h3>

            <button
              onClick={() => setFireResult(null)}
              className="text-xs text-slate-400 hover:text-slate-700"
            >
              CLOSE
            </button>
          </div>

          <div
            className={
              "text-sm px-3 py-3 border " +
              (
                fireResult?.routed_to === "broker"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              )
            }
          >
            {fireResult?.routed_to === "broker"
              ? "Orders were routed to the broker."
              : "Simulated — no live broker order was placed."}
          </div>

          {(fireResult?.orders || []).length > 0 && (
            <div className="mt-4 overflow-x-auto">

              <table className="w-full text-xs border border-slate-200">

                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Symbol</th>
                    <th className="px-3 py-2 text-left">Side</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-left">Broker</th>
                  </tr>
                </thead>

                <tbody>
                  {(fireResult.orders || []).map((o, i) => (
                    <tr
                      key={o.id || o.broker_order_id || i}
                      className="border-t border-slate-100"
                    >
                      <td className="px-3 py-2 font-mono">
                        {o.tradingsymbol ||
                          o.symbol ||
                          "—"}
                      </td>

                      <td className="px-3 py-2 font-bold">
                        {o.transaction_type ||
                          o.side ||
                          "—"}
                      </td>

                      <td className="px-3 py-2 text-right font-mono">
                        {o.quantity ??
                          o.qty ??
                          "—"}
                      </td>

                      <td className="px-3 py-2 text-right font-mono">
                        {money(
                          o.price ??
                          o.entry_price
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {o.broker_confirmed
                          ? `Broker #${o.broker_order_id || ""}`
                          : "Simulated"}
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>

            </div>
          )}

        </div>
      )}

    </div>
    </Layout>
  );
}
